import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import Select from "react-select";
import {
  LuAlertCircle,
  LuBuilding2,
  LuHome,
  LuListFilter,
  LuIndianRupee,
  LuMapPin,
  LuPhone,
  LuUser,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditParty,
  getIsPartyModalOpen,
  togglePartyModal,
} from "../../features/parties/partiesSlice";
import { partyTypes } from "../../static/partyType";
import { partyProcessTypes } from "../../static/processRate";
import { iconMultiSelectStyles, iconSelectStyles } from "../../static/styles";
import { IFormInput, OptionsProps } from "../../types/parties";
import { ShieldIcon } from "../icons";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  fieldTextareaClass,
  formFieldIconClass,
  formSelectIconClass,
} from "../ui/styles";

const inhouseOptions = [
  { value: 1, label: "હા" },
  { value: 0, label: "ના" },
];

type Props = {
  handleSaveParty: (data: IFormInput) => Promise<boolean>;
};

const PartyFormModal = ({ handleSaveParty }: Props) => {
  const dispatch = useAppDispatch();
  const editParty = useAppSelector(getEditParty);
  const isPartyModalOpen = useAppSelector(getIsPartyModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "સક્રિય",
    },
    {
      value: 0,
      label: "નિષ્ક્રિય",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      type: [],
      is_inhouse: 0,
      firm_name: "",
      name: "",
      phone: "",
      wp_phone: "",
      address: "",
      credit_limit: 0,
      status: 1,
    },
  });

  const selectedTypes = useWatch({ control, name: "type" });

  // Saved process types that are no longer ticked: saving removes this party's rates for them.
  const removedRateTypes =
    editParty?.id && editParty.items_count > 0
      ? partyProcessTypes(editParty.type).filter(
          (type) => !selectedTypes?.includes(type.value)
        )
      : [];

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    const saved = await handleSaveParty(data);
    setIsLoading(false);

    if (saved) {
      handleCloseFormModal();
    }
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(togglePartyModal(false));
  };

  useEffect(() => {
    if (editParty && editParty.id) {
      reset({
        type: editParty.type || [],
        is_inhouse: editParty.is_inhouse ?? 0,
        firm_name: editParty.firm_name,
        name: editParty.name,
        phone: editParty.phone,
        wp_phone: editParty.wp_phone,
        address: editParty.address,
        credit_limit: editParty.credit_limit ?? 0,
        status: editParty.status,
      });
    } else {
      reset({
        type: [],
        is_inhouse: 0,
        firm_name: "",
        name: "",
        phone: "",
        wp_phone: "",
        address: "",
        credit_limit: 0,
        status: 1,
      });
    }
  }, [editParty, isPartyModalOpen, reset]);

  return (
    <Dialog open={isPartyModalOpen === true} onClose={handleCloseFormModal}>
      <DialogContent
        size="md"
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>પાર્ટી ની માહિતી</h2>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(handleOnSubmit)}
          className={cx("space-y-4", dialogMobileFormClass)}
        >
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            <div className="space-y-2">
              <label className={fieldLabelClass}>
                પ્રકાર <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.type && "error")}>
                <ShieldIcon className={formSelectIconClass} />
                <Controller
                  {...register("type", { required: true })}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={partyTypes}
                      value={partyTypes.filter((option) =>
                        field.value?.includes(option.value)
                      )}
                      onChange={(val: any) =>
                        field.onChange(val?.map((v: any) => v.value) || [])
                      }
                      placeholder="પ્રકાર સિલેક્ટ કરો..."
                      styles={iconMultiSelectStyles}
                      isSearchable
                      isMulti
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.type && "error"
                      )}
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
              {errors.type && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પાર્ટી નો પ્રકાર સિલેક્ટ કરો
                </p>
              )}
              {removedRateTypes.length > 0 && (
                <p className="flex items-center text-sm text-orange-700">
                  <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                  સેવ કરતાં આ પાર્ટીના{" "}
                  {removedRateTypes.map((type) => type.label).join(", ")} ભાવ
                  કાઢી નાખવામાં આવશે
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>ઇનહાઉસ</label>
              <div className="relative">
                <LuHome className={formSelectIconClass} />
                <Controller
                  name="is_inhouse"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={
                        inhouseOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      options={inhouseOptions}
                      onChange={(val: any) =>
                        field.onChange(val ? val.value : 0)
                      }
                      placeholder="ઇનહાઉસ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                પેઢીનું નામ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.firm_name && "error")}>
                <LuBuilding2 className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="પેઢીનું નામ દાખલ કરો"
                  className={fieldInputClass(!!errors.firm_name)}
                  {...register("firm_name", { required: true })}
                />
                {errors.firm_name && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.firm_name && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  પેઢીનું નામ દાખલ કરો
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>પાર્ટીનું નામ</label>
              <div className="relative">
                <LuUser className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="પાર્ટીનું નામ દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("name", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>મોબાઇલ નંબર</label>
              <div className="relative">
                <LuPhone className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="મોબાઇલ નંબર દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("phone", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>વહાર્ટસપ નંબર</label>
              <div className="relative">
                <LuPhone className={formFieldIconClass} />
                <Input
                  type="text"
                  placeholder="વહાર્ટસપ નંબર દાખલ કરો"
                  className={fieldInputClass()}
                  {...register("wp_phone", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>એડ્રેસ</label>
              <div className="relative">
                <LuMapPin className={formFieldIconClass} />
                <Textarea
                  rows={3}
                  placeholder="પાર્ટીનુ એડ્રેસ દાખલ કરો"
                  className={fieldTextareaClass()}
                  {...register("address", { required: false })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                ક્રેડિટ લિમિટ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.credit_limit && "error")}>
                <LuIndianRupee className={formFieldIconClass} />
                <Input
                  type="number"
                  step="any"
                  placeholder="ક્રેડિટ લિમિટ દાખલ કરો"
                  className={fieldInputClass(!!errors.credit_limit)}
                  {...register("credit_limit", {
                    required: true,
                    valueAsNumber: true,
                    validate: (value) =>
                      Number.isFinite(Number(value)) && Number(value) >= 0,
                  })}
                />
                {errors.credit_limit && (
                  <LuAlertCircle className={fieldErrorIconClass} />
                )}
              </div>
              {errors.credit_limit && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  ક્રેડિટ લિમિટ દાખલ કરો
                </p>
              )}
              <p className="text-xs text-gray-500">
                0 રાખશો તો કોઈ લિમિટ નહીં, બાકી પેમેન્ટ લિમિટ થી વધે તો પેમેન્ટ
                આઇકન લાલ દેખાશે
              </p>
            </div>

            <div className="space-y-2">
              <label className={fieldLabelClass}>
                સ્ટેટ્સ <span className="text-red-500">*</span>
              </label>
              <div className={cx("relative", errors.status && "error")}>
                <LuListFilter className={formSelectIconClass} />
                <Controller
                  {...register("status", { required: true })}
                  control={control}
                  defaultValue={1}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      value={
                        statusOption.find((val) => val.value === field.value) ||
                        null
                      }
                      options={statusOption}
                      placeholder="સ્ટેટ્સ સિલેક્ટ કરો"
                      styles={iconSelectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll
                      classNames={{ menuPortal: () => "font-inter" }}
                      className={cx(
                        "react-select-container",
                        errors.status && "error"
                      )}
                      classNamePrefix="react-select"
                      onChange={(val: any) => setValue("status", val?.value)}
                    />
                  )}
                />
              </div>
              {errors.status && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  સ્ટેટ્સ સિલેક્ટ કરો
                </p>
              )}
            </div>
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              રદ કરો
            </Button>

            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <span className={buttonSpinnerClass}></span>
                  સાચવી રહ્યા છીએ...
                </span>
              ) : (
                "સાચવો"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartyFormModal;
